/**
 * Reusable post-processing presets extracted from existing level scenes.
 *
 * Each preset configures the DefaultRenderingPipeline: bloom, chromatic
 * aberration, film grain, vignette, image processing (contrast/exposure),
 * tone mapping, and colour grading curves.
 *
 * Tone mapping defaults to ACES across all presets because the previous
 * default (Standard) clipped bright highlights (the Atrium god-ray, Canopy
 * brass glow, Scrapyard emissive accents) to white. ACES preserves the
 * shoulder so emissives read as warm rather than blown.
 *
 * Colour grading pushes each level into a distinct palette:
 *   - Atrium: warm amber with lifted shadows (cosy sunroom)
 *   - Scrapyard: desaturated rust-green (heavy, stagnant, choked)
 *   - Conservatory: dual-state — UNDERGROUND_WARMTH is the Incubation
 *       baseline; the scene swaps to CONSERVATORY_IRRIGATION on phase
 *       change for cool-teal mist
 *   - Canopy: cool cosmic indigo with saturated brass highlights
 *
 * Phase C1 (2026-04-16) differentiated these presets so each level reads
 * as a distinct mood rather than four variants of "dusk purple".
 */
import { Color4 } from "@babylonjs/core/Maths/math.color";
import type { PostProcessPreset } from "../VisualProfile";

// ── Shared Defaults ────────────────────────────────────────────────────
//
// 2026-07-10 visual review, finding #2: chromatic aberration and film
// grain are REMOVED from every baseline preset. Stacked on double glow
// (GlowLayer + bloom) and pushed exposure, the always-on CA/grain passes
// smeared and noised the whole frame — the recurring "everything feels
// out of focus" feedback that per-preset softening (2.4 → 0.8 etc.)
// never actually resolved. Both effects remain available in the
// PostProcessPreset type for MOMENTS that want them (Elder reveals,
// visions, cutscene presets) — they just no longer tax every frame.

// ── Soft Glow — Liminal Atrium ─────────────────────────────────────────
// Cosy Victorian sunroom. Warm amber grade, lifted shadows, subtle
// chromatic aberration so the god-ray reads as a real beam. Bloom
// threshold stays high so only the emissive cue-lights bloom.

export const SOFT_GLOW: PostProcessPreset = {
  bloom: { threshold: 0.82, weight: 0.28, kernel: 40 },
  vignette: {
    weight: 2.2,
    cameraFov: 0.5,
    color: new Color4(0.06, 0.03, 0.08, 1),
  },
  imageProcessing: { contrast: 1.18, exposure: 1.18 },
  toneMapping: "aces",
  colorGrading: {
    // Warmer midtones, punchier jewel tones, shadows lifted a touch.
    globalHue: -6,
    globalSaturation: 12,
    globalDensity: -12,
    midtonesHue: -10,
    midtonesSaturation: 10,
  },
};

// ── Dense Atmosphere — Redwood Scrapyard ───────────────────────────────
// Twilight forest choked with rust. Desaturated overall, warm-rust
// midtones, deeper vignette. Chromatic aberration stronger to sell the
// "this world is slightly broken" feel of Level 1's narrative state.

export const DENSE_ATMOSPHERE: PostProcessPreset = {
  // Softened 2026-04-17 (CA 2.4 → 0.8, grain 3.2 → 1.6, vignette
  // 3.8 → 2.8), then CA/grain removed entirely 2026-07-10 — see header.
  // Bloom threshold raised 0.55 → 0.7 so only genuine emissives bloom
  // instead of the whole lit midtone range.
  bloom: { threshold: 0.7, weight: 0.24, kernel: 36 },
  vignette: {
    weight: 2.8,
    cameraFov: 0.5,
    color: new Color4(0.03, 0.02, 0.05, 1),
  },
  imageProcessing: { contrast: 1.2, exposure: 1.1 },
  toneMapping: "aces",
  colorGrading: {
    globalHue: -4,
    globalSaturation: -8,
    globalDensity: 4,
    midtonesHue: -12,
    midtonesSaturation: 10,
    shadowsSaturation: -16,
  },
};

// ── Underground Warmth — Overgrown Conservatory (Incubation) ───────────
// Default phase for L2. Hot, golden, oppressive. Exposure pushed,
// saturation amped, vignette warm-brown. Canon's Incubation state.

export const UNDERGROUND_WARMTH: PostProcessPreset = {
  // Incubation baseline. Kept the saturation/exposure swing that
  // makes the phase flip dramatic; CA/grain removed 2026-07-10 (see
  // header). Bloom threshold raised 0.3 → 0.65 — at 0.3 with exposure
  // 1.4 nearly every lit surface bloomed, washing the hot phase into
  // an orange smear.
  bloom: { threshold: 0.65, weight: 0.34, kernel: 36 },
  vignette: {
    weight: 2.6,
    cameraFov: 0.5,
    color: new Color4(0.14, 0.05, 0.02, 1),
  },
  imageProcessing: { contrast: 1.32, exposure: 1.4 },
  toneMapping: "aces",
  colorGrading: {
    globalHue: -22,
    globalSaturation: 30,
    globalDensity: -14,
    highlightsHue: -28,
    highlightsSaturation: 22,
    midtonesHue: -18,
    midtonesSaturation: 18,
  },
};

// ── Conservatory Irrigation — cool mist veil for the second phase ──────
// Not a baseline preset — the Conservatory scene swaps into this when
// CONSERVATORY_PHASE_CHANGED fires with phase === "irrigation". Cooler
// exposure, teal vignette, reduced saturation so the Nectar-Opossums'
// cover-by-mist read is unmistakable.

export const CONSERVATORY_IRRIGATION: PostProcessPreset = {
  // Irrigation state — the mist-and-shadow counterweight to the warm
  // incubation. Deepened versus prior tuning: exposure 0.94 → 0.82,
  // hue shift +22 → +35 for stronger cyan push, bloom softened so
  // highlights don't read as incubation warmth leaking through.
  // CA/grain removed 2026-07-10 (see header).
  bloom: { threshold: 0.7, weight: 0.18, kernel: 36 },
  vignette: {
    weight: 4.2,
    cameraFov: 0.5,
    color: new Color4(0.01, 0.06, 0.08, 1),   // Cool teal
  },
  imageProcessing: { contrast: 1.12, exposure: 0.82 },
  toneMapping: "aces",
  colorGrading: {
    globalHue: 35,
    globalSaturation: -18,
    globalDensity: 18,
    midtonesHue: 38,
    midtonesSaturation: -10,
    shadowsHue: 42,
    shadowsSaturation: -16,
  },
};

// ── Cinematic Night — Clockwork Canopy ─────────────────────────────────
// Starlit finale. Cool indigo grade, heavy vignette, stronger chromatic
// aberration on the brass lenses, tonemapped so emissive stars stay
// legible against the deep black sky without blowing out.

export const CINEMATIC_NIGHT: PostProcessPreset = {
  // L3 cinematic. The prior tuning (CA 3.6, bloom threshold 0.22)
  // bloomed every pixel and smeared chromatic fringes everywhere —
  // the orrery+stars cathedral was getting lost in noise. CA/grain
  // removed 2026-07-10 (see header); bloom threshold raised 0.35 →
  // 0.6 so stars and brass emissives still glow but lit geometry
  // stays crisp against the night sky.
  bloom: { threshold: 0.6, weight: 0.3, kernel: 40 },
  vignette: {
    weight: 3.2,
    cameraFov: 0.5,
    color: new Color4(0.01, 0.01, 0.05, 1),
  },
  imageProcessing: { contrast: 1.28, exposure: 1.18 },
  toneMapping: "aces",
  colorGrading: {
    globalHue: 14,
    globalSaturation: 8,
    globalDensity: 14,
    highlightsHue: -6,
    highlightsSaturation: 16,
    shadowsHue: 18,
    shadowsSaturation: -8,
  },
};
